class NodoTrie {
  constructor() {
    this.hijos = {};
    this.registros = new Set();
  }
}

export class IndiceBusquedaRapida {
  constructor(datosEntrante = [], cabecera = [], modo = 'NOR', claveRegistros = 'registros') {
    this.modo = String(modo).toUpperCase() === 'OPT' ? 'OPT' : 'NOR';
    this.claveRegistros = claveRegistros;
    this.estructuraOriginal = Array.isArray(datosEntrante) ? datosEntrante : [];
    this.datosOriginales = [];

    this._extraerRegistrosPlanos(this.estructuraOriginal);

    this.camposClave = Array.isArray(cabecera)
      ? cabecera.map((c) => (typeof c === 'object' ? c.valor : c)).filter(Boolean)
      : [];

    this.indice = {};

    const inicioConstruccion = performance.now();

    if (this.modo === 'OPT') {
      this._construirEstructuraTrie();
    }

    const finConstruccion = performance.now();

    this.tiempoConstruccionMs = (finConstruccion - inicioConstruccion).toFixed(3);
    this.memoriaConstruccionBytes = IndiceBusquedaRapida.estimarMemoriaTrieBytes(this.indice);
  }

  static limpiarTexto(texto) {
    if (texto === null || texto === undefined) return '';
    return String(texto)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  static evaluarCondicion(valorReg, valorFiltro, operador = '==', tipo = 'str') {
    if (valorFiltro === '' || valorFiltro === null || valorFiltro === undefined) {
      return true;
    }

    if (tipo === 'int' || tipo === 'float' || tipo === 'number') {
      const numReg = Number(valorReg);
      const numFiltro = Number(valorFiltro);

      if (isNaN(numReg) || isNaN(numFiltro)) return false;

      switch (operador) {
        case '==': return numReg === numFiltro;
        case '!=': return numReg !== numFiltro;
        case '>':  return numReg > numFiltro;
        case '>=': return numReg >= numFiltro;
        case '<':  return numReg < numFiltro;
        case '<=': return numReg <= numFiltro;
        default:   return numReg === numFiltro;
      }
    }

    if (tipo === 'date') {
      const fechaReg = new Date(valorReg).getTime();
      const fechaFiltro = new Date(valorFiltro).getTime();

      if (isNaN(fechaReg) || isNaN(fechaFiltro)) return false;

      switch (operador) {
        case '==': return fechaReg === fechaFiltro;
        case '!=': return fechaReg !== fechaFiltro;
        case '>':  return fechaReg > fechaFiltro;
        case '>=': return fechaReg >= fechaFiltro;
        case '<':  return fechaReg < fechaFiltro;
        case '<=': return fechaReg <= fechaFiltro;
        default:   return fechaReg === fechaFiltro;
      }
    }

    const txtReg = IndiceBusquedaRapida.limpiarTexto(valorReg);
    const txtFiltro = IndiceBusquedaRapida.limpiarTexto(valorFiltro);

    switch (operador) {
      case '==':       return txtReg === txtFiltro;
      case '!=':       return txtReg !== txtFiltro;
      case 'contains': return txtReg.includes(txtFiltro);
      case '>':        return txtReg > txtFiltro;
      case '>=':       return txtReg >= txtFiltro;
      case '<':        return txtReg < txtFiltro;
      case '<=':       return txtReg <= txtFiltro;
      default:         return txtReg.includes(txtFiltro);
    }
  }

  static estimarMemoriaTrieBytes(indice) {
    let totalBytes = 0;

    const calcularNodoBytes = (nodo) => {
      let bytes = 48;
      if (nodo.registros.size > 0) {
        bytes += 64 + (nodo.registros.size * 8);
      }
      for (const [caracter, nodoHijo] of Object.entries(nodo.hijos)) {
        bytes += 32;
        bytes += calcularNodoBytes(nodoHijo);
      }
      return bytes;
    };

    for (const [campo, raizCampo] of Object.entries(indice)) {
      if (raizCampo instanceof NodoTrie) {
        totalBytes += 160;
        totalBytes += calcularNodoBytes(raizCampo);
      }
    }

    return totalBytes;
  }

  static _formatearBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  _extraerRegistrosPlanos(datos) {
    if (!Array.isArray(datos) || datos.length === 0) {
      this.datosOriginales = [];
      return;
    }

    if (datos.some((item) => Array.isArray(item?.[this.claveRegistros]))) {
      this.datosOriginales = datos.flatMap((cat) =>
        Array.isArray(cat[this.claveRegistros]) ? cat[this.claveRegistros] : []
      );
    } else {
      this.estructuraOriginal = [{ categoria: 'General', [this.claveRegistros]: datos }];
      this.datosOriginales = datos;
    }
  }

  _construirEstructuraTrie() {
    this.camposClave.forEach((campo) => {
      this.indice[campo] = new NodoTrie();
    });

    this.datosOriginales.forEach((registro, posicion) => {
      this._indexarRegistro(registro, posicion);
    });
  }

  _indexarRegistro(registro, posicion) {
    this.camposClave.forEach((campo) => {
      const valorLimpio = IndiceBusquedaRapida.limpiarTexto(registro[campo]);
      if (!valorLimpio) return;

      const raizCampo = this.indice[campo];
      if (!raizCampo) return;

      const longitud = valorLimpio.length;

      for (let i = 0; i < longitud; i++) {
        let nodoActual = raizCampo;

        for (let j = i; j < longitud; j++) {
          const caracter = valorLimpio[j];

          if (!nodoActual.hijos[caracter]) {
            nodoActual.hijos[caracter] = new NodoTrie();
          }

          nodoActual = nodoActual.hijos[caracter];
          nodoActual.registros.add(posicion);
        }
      }
    });
  }

  _desindexarValorCampo(campo, valorAnterior, posicion) {
    const valorLimpio = IndiceBusquedaRapida.limpiarTexto(valorAnterior);
    if (!valorLimpio) return;

    const raizCampo = this.indice[campo];
    if (!raizCampo) return;

    const longitud = valorLimpio.length;

    for (let i = 0; i < longitud; i++) {
      let nodoActual = raizCampo;

      for (let j = i; j < longitud; j++) {
        const caracter = valorLimpio[j];
        if (!nodoActual.hijos[caracter]) break;

        nodoActual = nodoActual.hijos[caracter];
        nodoActual.registros.delete(posicion);
      }
    }
  }

  _indexarValorCampo(campo, nuevoValor, posicion) {
    const valorLimpio = IndiceBusquedaRapida.limpiarTexto(nuevoValor);
    if (!valorLimpio) return;

    if (!this.indice[campo]) {
      this.indice[campo] = new NodoTrie();
    }

    const raizCampo = this.indice[campo];
    const longitud = valorLimpio.length;

    for (let i = 0; i < longitud; i++) {
      let nodoActual = raizCampo;

      for (let j = i; j < longitud; j++) {
        const caracter = valorLimpio[j];

        if (!nodoActual.hijos[caracter]) {
          nodoActual.hijos[caracter] = new NodoTrie();
        }

        nodoActual = nodoActual.hijos[caracter];
        nodoActual.registros.add(posicion);
      }
    }
  }

  _buscarEnTrie(campo, busquedaLimpia) {
    let nodoActual = this.indice[campo];
    if (!nodoActual) return new Set();

    for (let i = 0; i < busquedaLimpia.length; i++) {
      const caracter = busquedaLimpia[i];
      if (!nodoActual.hijos[caracter]) {
        return new Set();
      }
      nodoActual = nodoActual.hijos[caracter];
    }

    return nodoActual.registros;
  }

  _coincideObjeto(objeto, campo, valor) {
    if (!objeto) return false;

    if (Array.isArray(campo) && Array.isArray(valor)) {
      return campo.every((key, idx) => {
        const valEsperado = valor[idx];
        return String(objeto[key]) === String(valEsperado);
      });
    }

    return String(objeto[campo]) === String(valor);
  }

  /**
   * Actualiza uno o múltiples campos de un registro.
   * Acepta primitivos o arreglos con correspondencia 1 a 1 en:
   * - (campoCat, valorCat)
   * - (campoReg, valorReg)
   * - (campoMutar, nuevoValor)
   */
  actualizarRegistro(campoCat, valorCat, campoReg, valorReg, campoMutar, nuevoValor) {
    // 1. Buscar categoría
    const categoriaEncontrada = this.estructuraOriginal.find((cat) =>
      this._coincideObjeto(cat, campoCat, valorCat)
    );

    if (!categoriaEncontrada || !Array.isArray(categoriaEncontrada[this.claveRegistros])) {
      return false;
    }

    const listaRegistros = categoriaEncontrada[this.claveRegistros];

    // 2. Buscar registro objetivo
    const registroObjetivo = listaRegistros.find((reg) =>
      this._coincideObjeto(reg, campoReg, valorReg)
    );

    if (!registroObjetivo) return false;

    // 3. Obtener el índice plano en datosOriginales
    const posicionPlana = this.datosOriginales.indexOf(registroObjetivo);
    if (posicionPlana === -1) return false;

    // 4. Normalizar campos a mutar y nuevos valores a arreglos para tratamiento uniforme
    const listaCampos = Array.isArray(campoMutar) ? campoMutar : [campoMutar];
    const listaValores = Array.isArray(nuevoValor) ? nuevoValor : [nuevoValor];

    // 5. Aplicar mutaciones y re-indexar en el Trie si corresponde
    listaCampos.forEach((campo, idx) => {
      const valNuevo = listaValores[idx];
      const valAnterior = registroObjetivo[campo];

      if (this.modo === 'OPT' && this.camposClave.includes(campo)) {
        this._desindexarValorCampo(campo, valAnterior, posicionPlana);
        registroObjetivo[campo] = valNuevo;
        this._indexarValorCampo(campo, valNuevo, posicionPlana);
      } else {
        registroObjetivo[campo] = valNuevo;
      }
    });

    return true;
  }

  filtrar(filtrosActivos = {}) {
    if (this.modo === 'OPT') {
      return this._filtrarOptimizado(filtrosActivos);
    }
    return this._filtrarNormal(filtrosActivos);
  }

  _filtrarNormal(filtrosActivos = {}) {
    if (!filtrosActivos || typeof filtrosActivos !== 'object') {
      return this.estructuraOriginal;
    }

    const criterios = Object.entries(filtrosActivos)
      .map(([campo, config]) => ({
        campo,
        valor: config?.valor ?? config,
        operador: config?.operador || 'contains',
        tipo: config?.tipo || 'str'
      }))
      .filter(({ valor }) => valor !== '' && valor !== null && valor !== undefined);

    if (criterios.length === 0) {
      return this.estructuraOriginal;
    }

    return this.estructuraOriginal
      .map((cat) => {
        const registrosFiltrados = (cat[this.claveRegistros] || []).filter((registro) =>
          criterios.every(({ campo, valor, operador, tipo }) =>
            IndiceBusquedaRapida.evaluarCondicion(registro[campo], valor, operador, tipo)
          )
        );

        return {
          ...cat,
          [this.claveRegistros]: registrosFiltrados
        };
      })
      .filter((cat) => cat[this.claveRegistros].length > 0);
  }

  _filtrarOptimizado(filtrosActivos = {}) {
    if (!filtrosActivos || typeof filtrosActivos !== 'object') {
      return this.estructuraOriginal;
    }

    const criterios = Object.entries(filtrosActivos)
      .map(([campo, config]) => ({
        campo,
        valor: config?.valor ?? config,
        operador: config?.operador || 'contains',
        tipo: config?.tipo || 'str'
      }))
      .filter(({ valor }) => valor !== '' && valor !== null && valor !== undefined);

    if (criterios.length === 0) {
      return this.estructuraOriginal;
    }

    let posicionesCoincidentes = null;

    for (const { campo, valor, operador, tipo } of criterios) {
      let coincidenciasActuales = new Set();

      if (tipo === 'str' && (operador === 'contains' || operador === '==')) {
        const busquedaLimpia = IndiceBusquedaRapida.limpiarTexto(valor);
        const candidatosTrie = this._buscarEnTrie(campo, busquedaLimpia);

        if (operador === '==') {
          candidatosTrie.forEach((pos) => {
            if (IndiceBusquedaRapida.limpiarTexto(this.datosOriginales[pos]?.[campo]) === busquedaLimpia) {
              coincidenciasActuales.add(pos);
            }
          });
        } else {
          coincidenciasActuales = candidatosTrie;
        }
      } else {
        this.datosOriginales.forEach((registro, posicion) => {
          if (IndiceBusquedaRapida.evaluarCondicion(registro[campo], valor, operador, tipo)) {
            coincidenciasActuales.add(posicion);
          }
        });
      }

      if (posicionesCoincidentes === null) {
        posicionesCoincidentes = new Set(coincidenciasActuales);
      } else {
        posicionesCoincidentes = new Set(
          [...posicionesCoincidentes].filter((pos) => coincidenciasActuales.has(pos))
        );
      }

      if (posicionesCoincidentes.size === 0) break;
    }

    const registrosUnicosSet = posicionesCoincidentes === null
      ? new Set(this.datosOriginales)
      : new Set(Array.from(posicionesCoincidentes).map((pos) => this.datosOriginales[pos]));

    return this.estructuraOriginal
      .map((cat) => {
        const registrosFiltrados = (cat[this.claveRegistros] || []).filter((reg) =>
          registrosUnicosSet.has(reg)
        );

        return {
          ...cat,
          [this.claveRegistros]: registrosFiltrados
        };
      })
      .filter((cat) => cat[this.claveRegistros].length > 0);
  }
}